<template>
  <section class="ml-4">
    <v-row no-gutters>
      <v-col class="d-flex justify-space-between offset-8" cols="4">
        <v-btn outlined class="ma-1" color="grey">
          <i class="fas fa-file-download"></i>
        </v-btn>
        <v-select
          v-model="selectedTimeSpan"
          :items="timeSpanSelectValues"
          item-value="value"
          item-text="text"
          outlined
          dense
          :label="$t('priceHistory.timespan')"
        ></v-select>
      </v-col>

      <v-col cols="12" class="">
        <price-history-chart
          v-if="!loading"
          chartId="priceHistory"
          :date-range="currentRange"
          :priceHistoryData="priceHistoryData"
          :selectedProductDefinitions="selectedProductDefinitions"
        >
        </price-history-chart>
        <v-skeleton-loader
          v-else
          class="mx-auto"
          type="card"
        ></v-skeleton-loader>
      </v-col>
    </v-row>
  </section>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
import DateRangeSlider from '@/components-internal/dateRange/DateRangeSlider'
import DateRange from '@/classes-shared/dates/DateRange'
import PriceHistoryChart from '@/components-internal/charts/PriceHistoryChart.vue'

export default {
  name: 'PriceHistory',

  components: {
    PriceHistoryChart,
  },

  props: {
    /**
     * The raw chart data
     */
    priceHistoryData: {
      type: Array,
      required: true,
    },

    /**
     * The day the prices were calculated
     */
    calculationDate: {
      type: [String, Date],
      required: true,
    },

    /**
     * The selected product
     * definitions the chart
     * contains data for
     */
    selectedProductDefinitions: {
      type: Array,
      required: true,
    },
  },

  data() {
    const maxDate = moment(this.calculationDate).isSameOrAfter(moment())
      ? moment()
      : moment(this.calculationDate)
    return {
      currentRange: new DateRange(
        maxDate.clone().subtract(30, 'days'),
        maxDate
      ),
      selectedTimeSpan: 30,
      loading: true,
    }
  },

  computed: {
    getDiffFirstAndLastDate() {
      const last = moment(this.getLastDateInData())
      const first = moment(this.getFirstDateInData())
      const diff = first.diff(last, 'days')
      return moment.duration(diff, 'days').asDays()
    },
    timeSpanSelectValues() {
      return [
        {
          text: this.$i18n.t('priceHistory.sevenDays'),
          value: 7,
        },
        {
          text: this.$i18n.t('priceHistory.fourteenDays'),
          value: 14,
        },
        {
          text: this.$i18n.t('priceHistory.thirtyDays'),
          value: 30,
        },
        {
          text: this.$i18n.t('priceHistory.all'),
          value: this.getDiffFirstAndLastDate,
        },
      ]
    },
  },

  watch: {
    priceHistoryData() {
      this.loading = false
    },
    selectedTimeSpan(value) {
      const endDate = moment(this.calculationDate).isSameOrAfter(moment())
        ? moment()
        : moment(this.calculationDate)

      const daysBack = endDate.clone().subtract(value, 'days')
      this.currentRange.update(daysBack.toDate(), endDate.toDate())
    },
    calculationDate(value) {
      this.loading = true
      this.$store.dispatch('OverviewPricing/loadPriceHistory', {
        date: value,
      })
      const endDate = moment(value).isSameOrAfter(moment())
        ? moment()
        : moment(value)
      const daysBack = endDate.clone().subtract(this.selectedTimeSpan, 'days')

      this.currentRange.update(daysBack.toDate(), endDate.toDate())
    },
  },
  mounted() {
    const endDate = moment(this.calculationDate).isSameOrAfter(moment())
      ? moment()
      : moment(this.calculationDate)

    const daysBack = endDate.clone().subtract(this.selectedTimeSpan, 'days')
    this.$store.dispatch('OverviewPricing/loadPriceHistory', {
      date: this.calculationDate,
    })

    this.currentRange.update(daysBack.toDate(), endDate.toDate())
  },
  methods: {
    getFirstDateInData() {
      return this.priceHistoryData[0]?.calculatedAt
    },

    getLastDateInData() {
      return this.priceHistoryData[this.priceHistoryData.length - 1]
        ?.calculatedAt
    },
  },
}
</script>
